import { useMemo, type ComponentProps, type ComponentType } from 'react';
import { Bell, ArrowRightLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { NavMain } from './components/NavMain';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { NavUser } from './components/NavUser';
import { NavSecondary } from './components/NavSecondary';
import { useUserStore } from '@/stores/userStore';
import { routes } from '@/router/routes';
import type { HasMetaRouteWithFullPath } from '@/router';
import { mapRoutesWithFullPath } from '@/router/path';
import { LogoElement } from './components/LogoElement';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { getHasPermissionRoutes } from '@/router/getHasPermissionRoutes';
import { useUploadTaskBadgeCount } from '@/features/material-upload';

// 主导航菜单项类型
export interface NavMainItem {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
  isActive?: boolean;
  items?: {
    title: string;
    url: string;
  }[];
}

// 次级导航菜单项类型
export interface NavSecondaryItem {
  title: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
  meta: string;
}

export interface NavSecondaryMessageItem {
  title: string;
  icon: ComponentType<{ className?: string }>;
}

const navSecondary = [] as NavSecondaryItem[];

/**
 * 公司主体展示栏
 */
function CompanyBar() {
  const navigate = useNavigate();
  const { userInfo } = useUserStore();
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const companyName = userInfo?.team?.companyName || '未选择主体';

  const handleSwitch = () => {
    navigate('/login?pageType=team');
  };

  if (isCollapsed) {
    return (
      <div className="px-2 py-4">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="w-full" onClick={handleSwitch}>
              <ArrowRightLeft className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{companyName}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className="mx-3 mb-4 px-3 py-2 rounded-md border bg-muted/30 flex items-center gap-2 cursor-pointer hover:bg-muted/60 transition-colors"
      onClick={handleSwitch}
    >
      <span className="flex-1 text-sm truncate">{companyName}</span>
      <Button
        variant="ghost"
        size="icon-sm"
        className="shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          handleSwitch();
        }}
      >
        <ArrowRightLeft className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useUserStore();
  const uploadBadgeCount = useUploadTaskBadgeCount();

  const navMainRoutes = useMemo(() => {
    const permissionRoutes = getHasPermissionRoutes(
      userInfo?.permissionCodes || [],
      routes[0].children || [],
      (route) => route.permCode !== undefined && route.path !== '' && !!route.meta
    );
    return mapRoutesWithFullPath(permissionRoutes) as HasMetaRouteWithFullPath[];
  }, [userInfo?.permissionCodes]);

  // 菜单角标：key 为菜单项 fullPath。上传任务角标挂在「任务中心 > 上传任务」上
  const badgeCounts = useMemo(
    () => ({ '/task-center/upload-task': uploadBadgeCount }),
    [uploadBadgeCount]
  );

  return (
    <Sidebar collapsible="compact" {...props}>
      {/* 侧边栏头部 */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:px-2.5 pt-2 pb-1 h-9 text-nowrap"
            >
              <LogoElement />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* 侧边栏内容 */}
      <SidebarContent>
        <NavMain
          routes={navMainRoutes}
          location={location}
          navigate={navigate}
          badgeCounts={badgeCounts}
        />
        <NavSecondary items={navSecondary} className="mt-auto" />
      </SidebarContent>

      {/* 公司主体展示栏 */}
      <CompanyBar />

      {/* 侧边栏底部 */}
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
